import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { Spinner, Center, useToast } from '@chakra-ui/react';

// ProtectedRoute component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);  // null initially (unknown status)
  const [loading, setLoading] = useState(true);  // Track loading state
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Make API call to check if user is authenticated
        const response = await axios.get('/api/check-session');
        setIsAuthenticated(response.data.isAuthenticated);
      } catch (error) {
        if (error.response?.status === 401) {
          // Try to refresh the token
          try {
            const refreshResponse = await axios.post('/api/refresh-token', {
              refreshToken: localStorage.getItem('refreshToken')
            });
            
            if (refreshResponse.data.success) {
              localStorage.setItem('token', refreshResponse.data.data.token);
              localStorage.setItem('refreshToken', refreshResponse.data.data.refreshToken);
              setIsAuthenticated(true);
              return;
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }
        
        setIsAuthenticated(false);
        toast({
          title: 'Session expired',
          description: 'Please log in again',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);  // Stop loading once we have the response
      }
    };

    checkAuthentication();  // Check authentication when component mounts
  }, [toast]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        if (error.response?.status === 401) {
          try {
            const refreshResponse = await axios.post('/api/refresh-token', {
              refreshToken: localStorage.getItem('refreshToken')
            });
            
            if (refreshResponse.data.success) {
              localStorage.setItem('token', refreshResponse.data.data.token);
              localStorage.setItem('refreshToken', refreshResponse.data.data.refreshToken);
              
              // Retry the original request
              const config = error.config;
              config.headers['Authorization'] = `Bearer ${refreshResponse.data.data.token}`;
              return axios(config);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            toast({
              title: 'Session expired',
              description: 'Please log in again',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            navigate('/login');
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup the interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, toast]);

  // Show a loading spinner while waiting for the authentication check
  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, allow access to the protected route
  return children;
};

export default ProtectedRoute;
